import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FieldConfigDTO, TreeTypeDefDTO } from '../../../../../../system/system-dtos';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeNestedDataSource } from '@angular/material/tree';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DataTypeComponent } from '../../../data-type-component';
import { MyErrorStateMatcher } from '../../../../../../common/field-validation/my-error-state-matcher';
import { FlatTreeControl } from '@angular/cdk/tree';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EntityGroupDetailsService } from '../../../../../views/entity-group/entity-group-details/entity-group-details.service';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, switchMap } from 'rxjs/operators';
import { ConfirmationModalComponent } from '../../../../../dialogs/confirmation-modal/confirmation-modal.component';
import { of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { AdminContextService } from '../../../../../../common/services/admin.context.service';
import { CohortDO } from '../../../../../../common/domain-model-dtos';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-tree-form-field',
  templateUrl: './tree-form-field.component.html',
  styleUrls: ['./tree-form-field.component.scss'],
  standalone: false,
})
export class TreeFormFieldComponent extends DataTypeComponent implements OnInit {
  @Input() fieldConfig: FieldConfigDTO;
  @Input() formGroup: UntypedFormGroup;
  @Input() fields: FieldConfigDTO[];
  @Input() canAdd: boolean;

  editMode: boolean;
  readOnly: boolean = false;
  data: any[];

  activeNode: any;
  hoveredNode: any;
  private editFields: FieldConfigDTO[];
  private viewFields: FieldConfigDTO[];
  private keyFields: FieldConfigDTO[];
  public formGroupSubset: UntypedFormGroup;
  public creatingNew: boolean = false;

  public activeNodeLoading = false;
  private cohort: CohortDO;

  loading = true;
  type: TreeTypeDefDTO;

  private _transformer = (node: any, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      entity: node,
      level: level,
    } as FlatNode;
  };

  treeControl = new FlatTreeControl<any>(
    (node) => node.level,
    (node) => node.expandable
  );
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  private expandedNodeSet: Set<string> = new Set<string>();

  constructor(
    private changesDetector: ChangeDetectorRef,
    private toastrService: ToastrService,
    private http: HttpClient,
    private entityGroupService: EntityGroupDetailsService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private adminContextService: AdminContextService
  ) {
    super();
    adminContextService.getCohortWatcher().subscribe((c) => (this.cohort = c));
  }

  ngOnInit() {
    this.load();
    const key = Object.keys(this.fieldConfig.treeOptions.types)[0];
    this.type = this.fieldConfig.treeOptions.types[key];
    this.editFields = this.fieldConfig.innerFields.filter((f) => !f.editHide);
    this.viewFields = this.fieldConfig.innerFields.filter((f) => !f.viewHide);
    this.keyFields = this.fieldConfig.innerFields.filter((f) => f.objectKey);
    this.formGroupSubset = new UntypedFormGroup({});
  }

  private load(): void {
    this.loading = true;
    const params = new HttpParams().set('iosPreview', String(this.readOnly));
    this.http.get<CasinoDashboard>('/casino/single/dashboard', { params }).subscribe((e) => {
      this.data = e.groupings.categories;
      this.rebuildTreeForData(this.data);

      const activeNodeId = this.route.snapshot.queryParams['tree_active_node'];
      const expanded: string = this.route.snapshot.queryParams['tree_expanded_nodes'];
      if (expanded) {
        expanded.split(',').forEach((id) => this.expandedNodeSet.add(id));
      }
      this.treeControl.dataNodes
        .filter((n) => {
          if (n.entity.id.toString() === activeNodeId) {
            this.reloadNode(n);
          }
          return this.expandedNodeSet.has(String(n.entity.id));
        })
        .forEach((n) => {
          this.treeControl.expand(n);
        });

      this.loading = false;
    });
  }

  hasChild = (_: number, node: any) => !!node.children && node.children.length > 0;

  clickOnNode(node: FlatNode) {
    if (node.active) {
      return;
    }
    this.reloadNode(node);
  }

  private reloadNode(node: FlatNode) {
    this.creatingNew = false;
    this.editMode = false;
    this.editMode = false;
    if (this.activeNode) {
      this.activeNode.active = false;
      this.activeNode = null;
    }

    setTimeout(() => {
      this.activeNode = node;

      of(
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            ...this.route.snapshot.queryParams,
            tree_active_node: this.activeNode.entity.id,
          },
        })
      ).subscribe();

      node.active = true;
      this.createFormGroupsIfNeededV2(this.activeNode.entity);
      this.changesDetector.detectChanges();
    }, 0);
  }

  getFields(): FieldConfigDTO[] {
    return this.editMode
      ? this.editFields
      : this.viewFields.filter((f) => {
          // Remove order and visible columns from the table view
          if (f.name === 'gameLink') {
            const innerFields = f.innerFields;
            for (let i = innerFields.length - 1; i >= 0; i--) {
              if (innerFields[i].name === 'order' || innerFields[i].name === 'visible') {
                innerFields.splice(i, 1);
              }
            }
          }
          if (f.name === 'iosAutofillGameLink') {
            const parentId = this.formGroupSubset.getRawValue()['parentId'];
            if (parentId !== 0) {
              return false;
            }
          }
          if (!f.ignoreIfNotPresent) {
            return true;
          }

          const value = this.formGroupSubset.getRawValue()[f.name];
          return value && Object.keys(value).length > 0;
        });
  }

  private createFormGroupsIfNeededV2(data: any) {
    Object.keys(this.formGroupSubset.controls).forEach((c) => this.formGroupSubset.removeControl(c));
    const formGroupBuilder = {};
    this.fieldConfig.innerFields.forEach((f) => {
      const formControl = this.getFormControlForField(f, data);
      this.formGroupSubset.setControl(f.name, formControl);
    });
  }

  private getFormControlForField(f: FieldConfigDTO, data: any) {
    const disabled = f.editReadOnly && !!eval(f.editReadOnly);
    const value = data === undefined ? undefined : data[f.name];

    const validators = MyErrorStateMatcher.convertToAngularValidators([f]);
    return new UntypedFormControl(
      {
        value,
        disabled,
      },
      validators
    );
  }

  editNode(node: any) {
    this.clickOnNode(node);
    this.editMode = true;
  }

  // simple copied from https://stackblitz.com/edit/mat-tree-with-drag-and-drop?file=app%2Ftree-flat-overview-example.ts

  visibleNodes(): any[] {
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    const result = [];

    const addExpandedChildren = (node: any, expanded: Set<string>) => {
      result.push(node);
      if (expanded.has(node.id)) {
        node.children.map((child) => addExpandedChildren(child, expanded));
      }
    };

    this.dataSource.data.forEach((node) => {
      addExpandedChildren(node, this.expandedNodeSet);
    });
    return result;
  }

  /**
   * Handle the drop - here we rearrange the data based on the drop event,
   * then rebuild the tree.
   * */
  drop(event: CdkDragDrop<string[]>) {
    // ignore drops outside of the tree
    if (!event.isPointerOverContainer) return;

    // construct a list of visible nodes, this will match the DOM.
    // the cdkDragDrop event.currentIndex jives with visible nodes.
    // it calls rememberExpandedTreeNodes to persist expand state
    const visibleNodes = this.visibleNodes();

    // deep clone the data source so we can mutate it
    const changedData = JSON.parse(JSON.stringify(this.dataSource.data));

    // recursive find function to find siblings of node
    const findNodeSiblings = (arr: Array<any>, id: string) => {
      let result, subResult;
      arr.forEach((item) => {
        if (item.id === id) {
          result = arr;
        } else if (item.children) {
          subResult = findNodeSiblings(item.children, id);
          if (subResult) result = subResult;
        }
      });
      return result;
    };

    // remove the node from its old place
    const node: FlatNode = event.item.data;
    const siblings = findNodeSiblings(changedData, node.entity.id);
    const siblingIndex = siblings.findIndex((n) => n.id === node.entity.id);
    const nodeToInsert: any = siblings.splice(siblingIndex, 1)[0];

    // determine where to insert the node
    const nodeAtDest = visibleNodes[event.currentIndex];
    if (nodeAtDest.parentId !== node.entity.parentId) {
      this.toastrService.error('You can reorder items on one level for one parent only!');
      return;
    }
    if (nodeAtDest.id === nodeToInsert.id) return;

    // determine drop index relative to destination array
    let relativeIndex = event.currentIndex; // default if no parent
    const nodeAtDestFlatNode = this.treeControl.dataNodes.find((n) => nodeAtDest.id === n.entity.id);
    const parent = this.getParentNode(nodeAtDestFlatNode);

    if (parent) {
      const parentIndex = visibleNodes.findIndex((n) => n.id === parent.entity.id) + 1;
      relativeIndex = event.currentIndex - parentIndex;
    } else {
      const expandedRows = visibleNodes.slice(0, relativeIndex).filter((e) => e.parentId > 0).length;
      relativeIndex -= expandedRows;
    }
    // insert node
    const newSiblings = findNodeSiblings(changedData, nodeAtDest.id);
    if (!newSiblings) return;
    newSiblings.splice(relativeIndex, 0, nodeToInsert);

    const url = this.type.reorderUrl;
    this.rebuildTreeForData(changedData);

    this.notifyServerOnReorder(url, newSiblings);

    // rebuild tree with mutated data
  }

  private notifyServerOnReorder(url: string, newSiblings: any[]) {
    const request = newSiblings.map((element) => {
      const requestElement = {};
      this.keyFields.forEach((keyField) => {
        requestElement[keyField.name] = element[keyField.name];
      });
      return requestElement;
    });

    this.http.post(url, request).subscribe({
      error: () => {
        this.entityGroupService.reloadSubject.next(this.route.snapshot.paramMap);
      },
    });
  }

  /**
   * The following methods are for persisting the tree expand state
   * after being rebuilt
   */

  rebuildTreeForData(data: any) {
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    this.dataSource.data = data;
    this.forgetMissingExpandedNodes(this.treeControl, this.expandedNodeSet);
    this.expandNodesById(this.treeControl.dataNodes, Array.from(this.expandedNodeSet));
  }

  private rememberExpandedTreeNodes(treeControl: FlatTreeControl<FlatNode>, expandedNodeSet: Set<string>) {
    if (treeControl.dataNodes) {
      treeControl.dataNodes.forEach((node) => {
        if (treeControl.isExpandable(node) && treeControl.isExpanded(node)) {
          // capture latest expanded state
          expandedNodeSet.add(node.entity.id);
        }
      });
    }
  }

  private forgetMissingExpandedNodes(treeControl: FlatTreeControl<FlatNode>, expandedNodeSet: Set<string>) {
    if (treeControl.dataNodes) {
      expandedNodeSet.forEach((nodeId) => {
        // maintain expanded node state
        if (!treeControl.dataNodes.find((n) => n.entity.id === nodeId)) {
          // if the tree doesn't have the previous node, remove it from the expanded list
          expandedNodeSet.delete(nodeId);
        }
      });
    }
  }

  private expandNodesById(flatNodes: FlatNode[], ids: string[]) {
    if (!flatNodes || flatNodes.length === 0) return;
    const idSet = new Set(ids);
    return flatNodes.forEach((node) => {
      if (idSet.has(node.entity.id)) {
        this.treeControl.expand(node);
        let parent = this.getParentNode(node);
        while (parent) {
          this.treeControl.expand(parent);
          parent = this.getParentNode(parent);
        }
      }
    });
  }

  private getParentNode(node: FlatNode): FlatNode | null {
    const currentLevel = node.level;
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  mouseEntered(node: any) {
    node.hovered = true;
    this.hoveredNode = node;
  }

  mouseLeft(node: any) {
    node.hovered = false;
    if (node.entity.id === this.hoveredNode.entity.id) {
      this.hoveredNode = null;
    }
  }

  cancelUpdating() {
    this.creatingNew = false;
    this.editMode = false;
  }

  updateActiveNode() {
    const body = {
      ...this.activeNode.entity,
      ...this.formGroupSubset.getRawValue(),
    };

    const url = this.type.updateUrl.replace('{id}', this.activeNode.entity.id);
    this.activeNodeLoading = true;
    this.http.put(url, body).subscribe(
      (data) => {
        this.activeNode.entity = data;
        this.reloadNode(this.activeNode);
        this.toastrService.success('Category updated');

        if (this.type?.refreshViewAfterUpdate) {
          this.entityGroupService.reloadSubject.next(this.route.snapshot.paramMap);
        }
      },
      (error) => {
        this.activeNodeLoading = false;
      },
      () => (this.activeNodeLoading = false)
    );
  }

  newForParent(parentId: number, event: Event) {
    event.stopPropagation();
    if (this.activeNode) {
      this.activeNode.active = false;
    }
    this.activeNode = null;
    setTimeout(() => {
      const newEntity = { parentId, cohortId: this.cohort.id };
      this.creatingNew = true;
      this.editMode = true;
      this.createFormGroupsIfNeededV2(newEntity);
      this.changesDetector.detectChanges();
    }, 0);
  }

  createNew() {
    const url = this.type.addUrl;
    this.http.post(url, this.formGroupSubset.getRawValue()).subscribe(
      () => {
        this.entityGroupService.reloadSubject.next(this.route.snapshot.paramMap);
      },
      (error) => console.error('There was error!', error)
    );
  }

  delete(node: any) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        note: 'Are you sure you want to delete that category?',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((d) => !!d),
        switchMap(() => {
          const key = Object.keys(this.fieldConfig.treeOptions.types)[0];
          const url = this.fieldConfig.treeOptions.types[key].deleteUrl.replace('{id}', node.entity.id);
          return this.http.post(url, node.entity);
        })
      )
      .subscribe(
        () => {
          this.entityGroupService.reloadSubject.next(this.route.snapshot.paramMap);
        },
        (error) => console.log('There was error!', error)
      );
  }

  onNodeToggle(node: FlatNode) {
    if (this.treeControl.isExpanded(node)) {
      this.expandedNodeSet.add(node.entity.id);
    } else {
      this.expandedNodeSet.delete(node.entity.id);
    }

    of(
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          ...this.route.snapshot.queryParams,
          tree_expanded_nodes:
            this.expandedNodeSet.size === 0
              ? undefined
              : Array.from(this.expandedNodeSet).reduce((a, b) => a + ',' + b),
        },
      })
    ).subscribe();
  }

  switchMode($event: MatSlideToggleChange): void {
    this.readOnly = $event.checked;
    this.activeNode = null;
    this.load();
  }
}

export interface FlatNode {
  expandable: boolean;
  entity: any;
  level: number;
  active?: boolean;
}

export interface CasinoDashboard {
  groupings: CasinoDashboardCategories;
}

export interface CasinoDashboardCategories {
  categories: any[];
}
